import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { FilterValues } from "../../../../data-model/filterValues";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { createFilterPayloadForMyPaOrdersForPostgres } from "../utils/generateFilterPayloadForMyPaOrders";
import { useFilterPayload } from "./useFilterPayload";
import { MyMedicalPaOrdersState } from "./useMyPaOrdersTableData";

/**
 * Handles infinite-scroll pagination for My PA Orders. When the table's visible
 * end index reaches the last loaded hit and more pages are available, automatically
 * fetches the next page and appends the results.
 *
 * @param validFilters - Currently applied filter values
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param searchTerm - Free-text search string
 * @param myMedicalPaOrdersState - Current orders state (used to determine page boundaries)
 * @param setMyMedicalPaOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @returns endIndex and setEndIndex to track the table's visible boundary
 */
export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  initialDataFetchingExecuted: boolean,
  searchTerm: string,
  myMedicalPaOrdersState: MyMedicalPaOrdersState,
  setMyMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { setTableDataForContext, getTableDataFromContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext(
    TableNames.MY_PA_ORDERS_FROM_ALGOLIA,
  );
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [endIndex, setEndIndex] = useState(0);
  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
  );

  // Use refs to store latest values without triggering re-renders
  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setMyMedicalPaOrdersStateRef = useRef(setMyMedicalPaOrdersState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  // Keep refs updated
  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setMyMedicalPaOrdersStateRef.current = setMyMedicalPaOrdersState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setMyMedicalPaOrdersState,
    setTableData,
    setTableDataForContext,
  ]);

  const shouldFetchMoreData = useMemo(() => {
    return (
      endIndex === (myMedicalPaOrdersState.data?.hits?.length ?? 0) - 1 &&
      (myMedicalPaOrdersState.data?.page ?? 0) <
        (myMedicalPaOrdersState.data?.total_pages ?? 0) - 1
    );
  }, [
    endIndex,
    myMedicalPaOrdersState.data?.hits?.length,
    myMedicalPaOrdersState.data?.page,
    myMedicalPaOrdersState.data?.total_pages,
  ]);

  const payloadFinal = useMemo(() => {
    if (filterPayload === null) {
      return createFilterPayloadForMyPaOrdersForPostgres(
        validFilters,
        searchTerm,
        getOrgIdForFetchExternalWorklist(),
        user?.id ?? "",
        sortByField,
        sortAscending,
      );
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    searchTerm,
    user?.id,
    sortByField,
    sortAscending,
  ]);

  const payloadFinalWithPage =
    useMemo<SearchForMedicalPaOrdersFromPostgresParams>(
      () => ({
        ...payloadFinal,
        page: (myMedicalPaOrdersState.data?.page ?? 0) + 1,
      }),
      [payloadFinal, myMedicalPaOrdersState.data?.page],
    );

  const currentPage = myMedicalPaOrdersState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      authStatusOptions as AuthStatusOptionModel[],
      setMyMedicalPaOrdersStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.MY_PA_ORDERS_FROM_ALGOLIA, {
      lastBatchFetched: currentPage + 1,
    });
  }, [payloadFinalWithPage, authStatusOptions, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
